/** A time-series pane below the price chart — the §9b primitive.
 *
 *  §9b defines chart *overlays* (hline / priceRange / path) and a static side
 *  panel, but nothing that plots its own series against the price chart's time
 *  axis. This is that missing shape: give it `[(time, value)]` and it renders a
 *  line or a histogram, sharing the price chart's logical range.
 *
 *  It inherits the two §9b invariants:
 *    - logical-index alignment, so gaps don't shear it out of step with price
 *    - lazy/windowed data, supplied by the caller for the visible range
 *
 *  Deliberately dumb: it owns no fetching and no cumulation. CVD anchoring and
 *  OI colouring are decisions for the caller, because they depend on data this
 *  component has no business knowing about.
 */
import { useEffect, useRef } from "react";
import {
  createChart,
  ColorType,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
  type LogicalRange,
  type UTCTimestamp,
} from "lightweight-charts";

import { subscribeRange } from "@/utils/chartSync";
import styles from "./GCPanels.module.css";

export interface PanePoint {
  time: number;
  /** `null` emits a *whitespace* point: the bar slot exists (so this pane keeps
   *  the price chart's exact indexing) but nothing is drawn there. That is what
   *  makes a gap read as "no data" instead of a zero-filled flat line, which for
   *  CVD would assert balanced two-way flow that nobody traded. */
  value: number | null;
  /** Histogram only — per-bar colour. Lets the caller encode meaning (e.g. the
   *  price/OI quadrant) without this component knowing what it means. */
  color?: string;
}

interface Props {
  /** Chart tab id — the sync group. Two tabs never share a range. */
  groupId: string;
  /** Unique within the group. */
  paneId: string;
  title: string;
  subtitle?: string;
  data: PanePoint[];
  kind: "line" | "histogram";
  color: string;
  height: number;
  /** Draw a zero line. Meaningful for delta/CVD, not for absolute OI. */
  zeroLine?: boolean;
  /** Right-hand axis label for the latest value. */
  lastValueLabel?: string;
  empty?: string;
}

export function TimeSeriesPane({
  groupId,
  paneId,
  title,
  subtitle,
  data,
  kind,
  color,
  height,
  zeroLine = false,
  lastValueLabel,
  empty,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Line" | "Histogram"> | null>(null);

  // Create once per mount. Data flows in through the separate effect below so a
  // data change never tears down the chart (see issue #184: remounting a chart
  // whose data is already cached renders blank).
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const css = getComputedStyle(document.documentElement);
    const text = css.getPropertyValue("--text-dim").trim() || "#888";
    const grid = css.getPropertyValue("--border").trim() || "#2a2a2a";

    const chart = createChart(host, {
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: text,
        fontSize: 10,
      },
      grid: {
        vertLines: { color: grid, style: LineStyle.Dotted },
        horzLines: { color: grid, style: LineStyle.Dotted },
      },
      rightPriceScale: { borderColor: grid, scaleMargins: { top: 0.15, bottom: 0.1 } },
      // The price chart owns the visible time axis; a second one would just be
      // duplicated furniture stealing vertical space.
      timeScale: { visible: false, borderColor: grid },
      crosshair: { horzLine: { visible: false }, vertLine: { labelVisible: false } },
      // A pane has no independent time axis to navigate — it is a second view of
      // the price chart's window. Letting it pan or zoom on its own would put it
      // out of correspondence with the chart while still looking authoritative,
      // so the price chart is the only thing that can move the window.
      handleScroll: false,
      handleScale: false,
    });
    chartRef.current = chart;

    seriesRef.current =
      kind === "histogram"
        ? chart.addHistogramSeries({
            color,
            priceLineVisible: false,
            lastValueVisible: Boolean(lastValueLabel),
            priceFormat: { type: "volume" },
          })
        : chart.addLineSeries({
            color,
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: Boolean(lastValueLabel),
          });

    if (zeroLine) {
      seriesRef.current.createPriceLine({
        price: 0,
        color: grid,
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: false,
        title: "",
      });
    }

    // Receive-only. The pane never publishes a range: it cannot be moved by the
    // user, and echoing back the range it was just given is how two synced
    // charts end up fighting each other.
    const unsub = subscribeRange(groupId, paneId, (r: LogicalRange) => {
      try {
        chart.timeScale().setVisibleLogicalRange(r);
      } catch {
        /* range can be momentarily invalid mid-update */
      }
    });

    const ro = new ResizeObserver(() => {
      const el = hostRef.current;
      if (el) chart.applyOptions({ width: el.clientWidth, height: el.clientHeight });
    });
    ro.observe(host);
    chart.applyOptions({ width: host.clientWidth, height: host.clientHeight });

    return () => {
      ro.disconnect();
      unsub();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
    // Re-creating on `kind`/`color` change is fine — they don't change in practice.
  }, [groupId, paneId, kind, color, zeroLine, lastValueLabel]);

  useEffect(() => {
    const s = seriesRef.current;
    if (!s) return;
    // A null value becomes a whitespace point — the slot is kept so this pane's
    // logical indexing stays identical to the price chart's, but nothing is
    // plotted. Identical indexing is precisely what makes logical-range sync
    // exact rather than approximate.
    s.setData(
      data.map((p) =>
        p.value == null
          ? { time: p.time as UTCTimestamp }
          : {
              time: p.time as UTCTimestamp,
              value: p.value,
              ...(p.color ? { color: p.color } : {}),
            },
      ) as never,
    );
  }, [data]);

  return (
    <div className={styles.pane} style={{ height }}>
      <div className={styles.paneHeader}>
        <span className={styles.paneTitle}>{title}</span>
        {subtitle ? <span className={styles.paneSubtitle}>{subtitle}</span> : null}
      </div>
      <div ref={hostRef} className={styles.paneChart} />
      {data.length === 0 && empty ? (
        <div className={styles.paneEmpty}>{empty}</div>
      ) : null}
    </div>
  );
}
